<template>
  <div class="relative min-h-screen overflow-hidden">
    <!-- Background Image with multiply blend effect -->
    <div class="fixed inset-0 -z-30">
      <img src="/assets/photo.jpg" alt="Background" class="w-full h-full object-cover opacity-50 bg-image-animate" />
    </div>
    
    <!-- Primary multiply blend overlay for rich color interaction -->
    <div class="fixed inset-0 -z-20 bg-gradient-to-br from-blue-900/70 via-blue-900/70 to-gray-900/70 mix-blend-multiply"></div>
    
    <!-- Secondary overlay for better text readability -->
    <div class="fixed inset-0 -z-10 bg-black/40 backdrop-blur-[2px]"></div>
    
    <!-- Additional dark base layer -->
    <div class="fixed inset-0 bg-gray-900" style="z-index: -40;"></div>

    <!-- Navigation Header Component -->
    <NavHeader
      :dealerName="
        accountData && accountData[0]?.name ? accountData[0].name : dealerName
      "
    />

    <!-- Loader -->
    <div
      v-if="isLoading"
      class="isLoading flex justify-center items-center min-h-screen"
    >
      <div role="status">
        <svg
          aria-hidden="true"
          class="w-14 h-14 text-gray-600 animate-spin dark:text-gray-600 fill-blue-600"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <!-- Page Header -->
    <div class="relative z-1 pt-8 pb-2">
      <div class="max-w-7xl px-4 sm:px-6 lg:px-8 mx-auto">
        <div class="text-center mb-4">
          <h1
            class="text-3xl md:text-5xl font-bold text-white mb-3 bg-gradient-to-r from-blue-400 via-blue-500 to-emerald-400 bg-clip-text text-transparent"
          >
            Customer Communication Insights
          </h1>
          <p class="text-gray-300 text-lg mb-3">
            Track and monitor all customer touchpoints across email and SMS channels
          </p>
          <div class="flex sm:inline-flex flex-wrap items-center justify-center gap-x-2 gap-y-1 px-5 py-3 bg-blue-500/20 border border-blue-400/50 rounded-full w-full sm:w-auto max-w-md sm:max-w-none mx-auto">
            <svg class="w-5 h-5 text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
            </svg>
            <span class="text-sm sm:text-base text-blue-400 font-semibold whitespace-nowrap">{{ formattedDateRange }}</span>
            <span class="hidden sm:inline text-blue-400/60">•</span>
            <span class="text-xs sm:text-sm text-blue-400/80 whitespace-nowrap">Last 30 Days</span>
          </div>
        </div>

      </div>
    </div>


    <!-- Communication Tracking Component -->
    <div
      v-if="chartData && chartData.total_count"
      class="max-w-7xl px-4 pt-2 pb-6 sm:px-6 lg:px-8 mx-auto relative z-1"
    >
      <CommunicationTrackingComponent
        :accountSlug="$route.params.slug"
        :submissions="chartData.entries"
        :formId="accountData && accountData[0]?.form"
      />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="!isLoading"
      class="max-w-7xl px-4 pt-2 pb-6 sm:px-6 lg:px-8 mx-auto relative z-1"
    >
      <div class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-3xl p-12 text-center shadow-2xl">
        <svg class="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
        </svg>
        <h3 class="text-xl font-bold text-white mb-2">No Data Available</h3>
        <p class="text-gray-300 mb-8">
          No communication submissions found for the selected date range.
        </p>
        
        <!-- Submit Button (from form) -->
        <router-link
          :to="'/account/' + $route.params.slug"
          class="inline-flex justify-center items-center gap-4 py-3 px-8 bg-blue-600 rounded-full hover:bg-blue-700 transition-all duration-300 shadow-lg"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke-width="1.5"
            stroke="currentColor" 
            class="flex-shrink-0 h-10 w-10 text-white"
          >
            <path 
              stroke-linecap="round" 
              stroke-linejoin="round"
              d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" 
            />
          </svg>
          <span>
            <span class="block text-md font-semibold text-white">
              Send Your Onboarding
            </span>
            <span class="block text-xs text-white/80">
              Message Delivery
            </span>
          </span>
        </router-link>
      </div>
    </div>

  </div>
</template>

<script>
import NavHeader from "@/components/NavHeader.vue";
import CommunicationTrackingComponent from "@/components/CommunicationTracking.vue";
import axios from "axios";
import { mapState } from "vuex";

export default {
  name: "CommunicationTrackingView",
  components: {
    NavHeader,
    CommunicationTrackingComponent
  },
  data() {
    return {
      isLoading: true,
      chartData: null
    };
  },
  computed: {
    ...mapState(['accountData']),
    dealerName() {
      // Get dealer name from slug - convert slug format to display format
      const slug = this.$route.params.slug;
      if (slug) {
        // Convert slug like "courtney-patterson-ford" to "Courtney Patterson Ford"
        return slug
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      }
      return "Dealership";
    },
    formattedDateRange() {
      // Calculate the date range (last 30 days)
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      
      const formatDate = (date) => {
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
      };
      
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
      async fetchData() {
        this.isLoading = true;
        try {
          // Ensure account data is loaded in Vuex store first
          if (!this.accountData || !this.accountData[0]) {
            await this.$store.dispatch('fetchAccountData', this.$route.params.slug);
          }
          
          // Fetch all form submissions (accountData comes from Vuex store)
          await this.fetchChartData();
        } catch (error) {
          // Error fetching data
        } finally {
          this.isLoading = false;
        }
      },

    async fetchChartData() {
      try {
        // Get form ID from Vuex store (populated by store's fetchAccountData action)
        const formId = this.accountData && this.accountData[0]?.form;

        if (!formId) {
          this.chartData = { total_count: 0, entries: [] };
          return;
        }

        // Calculate date range - last 30 days
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 30); // 30 days back
        
        const formatDate = (date) => {
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        };

        const searchCriteria = {
          start_date: formatDate(startDate),
          end_date: formatDate(endDate)
        };

        // Fetch from Gravity Forms API with date filter
        const GF_CONSUMER_KEY = 'ck_9eb5fc380da548b29cd454ad4e4e40edbf6f8aad';
        const GF_CONSUMER_SECRET = 'cs_15706f39bc2a94c26ad71e695a2e730e29f6a7ae';

        const response = await axios.get(
          `https://engagr.driveagent.io/wp-json/gf/v2/forms/${formId}/entries`,
          {
            params: {
              consumer_key: GF_CONSUMER_KEY,
              consumer_secret: GF_CONSUMER_SECRET,
              search: JSON.stringify(searchCriteria),
              paging: { page_size: 1000 }
            }
          }
        );

        // Transform Gravity Forms response to match expected structure
        this.chartData = {
          total_count: response.data.total_count || 0,
          entries: response.data.entries || []
        };
      } catch (error) {
        this.chartData = { total_count: 0, entries: [] };
      }
    }
  }
};
</script>

<style scoped>
/* Background image subtle zoom animation (matching form.vue) */
@keyframes subtle-zoom {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

.bg-image-animate {
  animation: subtle-zoom 30s ease-in-out infinite;
}
</style>

